import type { ReactNode } from "react";
import { DataTableRow, ExpandedRow } from "./DataTableRow";
import type { Column, DataTableProps } from "./DataTableTypes";
import type { ExpandedRows } from "./useExpandedRows";

export interface DataTableRowsProps<R> extends Pick<DataTableProps<R>, "rowKey" | "onRowClick" | "renderExpanded"> {
  /** Rows of the current page. */
  rows: R[];
  columns: Column<R>[];
  query: string;
  expandedRows: ExpandedRows;
}

/** The page's rows, each followed by its opened content when it is expanded. */
export function DataTableRows<R>(props: DataTableRowsProps<R>): React.JSX.Element {
  const { rows, columns, query, rowKey, expandedRows, onRowClick, renderExpanded } = props;
  const keyOf = (row: R): string => (typeof rowKey === "function" ? rowKey(row) : String(row[rowKey]));

  /** Expandable rows toggle; otherwise a click handler, if any, runs. */
  function activation(row: R, key: string): (() => void) | undefined {
    if (renderExpanded) return () => expandedRows.toggle(key);
    if (onRowClick) return () => onRowClick(row);
    return undefined;
  }

  return (
    <>
      {rows.flatMap((row, index): ReactNode[] => {
        const key = keyOf(row);
        const open = expandedRows.isOpen(key);
        const look = { expandable: Boolean(renderExpanded), clickable: Boolean(onRowClick), open };
        const main = (
          <DataTableRow key={key} row={row} columns={columns} query={query} index={index} look={look} onActivate={activation(row, key)} />
        );
        if (!renderExpanded || !open) return [main];
        return [
          main,
          <ExpandedRow key={`${key}-detail`} colSpan={columns.length}>
            {renderExpanded(row)}
          </ExpandedRow>,
        ];
      })}
    </>
  );
}
